// Audit-package workflow — single source of truth for the lean post-submission state machine.
// States: Draft · Pending Office Review · Returned for Revision · Pending WSHC Outcome · Completed.
// Owner and next-action are DERIVED here so Dashboard / Jobs / My Audits / JobDetail never diverge.

export const WORKFLOW_STATES = ['Draft', 'Pending Office Review', 'Returned for Revision', 'Pending WSHC Outcome', 'Completed']

// The package's display state. Falls back for legacy jobs that predate workflow_state:
// a submitted-but-unkeyed report reads as Pending Office Review; otherwise null (use the pipeline status).
export function workflowState(job) {
  if (!job) return null
  if (job.workflow_state) return job.workflow_state
  if (job.report_submitted && job.status !== 'Completed') return 'Pending Office Review'
  if (job.status === 'Completed') return 'Completed'
  return null
}

// DISPLAY label for the status badge — derives the sub-states inside "Pending Office Review" (which the DB
// state machine doesn't split): approved-awaiting-send → "Ready to Send Report"; client-signed-awaiting-WSHC
// → "Ready for WSHC Submission". Uses only fields present on the job, so it works in lists and detail alike.
// The raw workflowState() is unchanged (used by queues/switches).
export function displayState(job) {
  const ws = workflowState(job)
  if (ws !== 'Pending Office Review') return ws
  if (!job.office_review_at) return 'Pending Office Review'
  const signed = !!job.report_acknowledged_at && job.report_acknowledged_version === job.report_ack_version
  if (signed) return 'Ready for WSHC Submission'
  // Approved-but-not-signed splits into "Ready to Send" (link not yet sent) vs "Awaiting Client
  // Acknowledgement" (link sent) so the badge and the next-action never read as a contradiction.
  return job.report_ack_sent_at ? 'Awaiting Client Acknowledgement' : 'Ready to Send Report'
}

// Owner = who must act next. Decision-Maker at the review gate; the routed office handler once approved.
export function ownerOf(job) {
  switch (displayState(job)) {
    case 'Draft':
    case 'Returned for Revision': return 'Auditor'
    case 'Pending Office Review': return 'Decision-Maker'
    case 'Ready to Send Report':
    case 'Awaiting Client Acknowledgement':
    case 'Ready for WSHC Submission':
    case 'Pending WSHC Outcome': return job.assigned_to_name || 'Office'
    case 'Completed': return 'Closed'
    default: return null
  }
}

export function nextActionOf(job, clientAcked) {
  switch (workflowState(job)) {
    case 'Draft': return 'Complete and submit package'
    case 'Pending Office Review':
      if (!job.office_review_at) return 'Review & approve, then send the report to the client (or return to auditor)'
      if (clientAcked) return 'Submit to WSHC'
      return job.report_ack_sent_at ? 'Awaiting client acknowledgement' : 'Send the acknowledgement link to the client'
    case 'Returned for Revision': return 'Revise and resubmit'
    case 'Pending WSHC Outcome': return 'Record WSHC outcome'
    case 'Completed': return null
    default: return null
  }
}

// Colour token for a state chip (bg + text).
export function stateChip(state) {
  switch (state) {
    case 'Pending Office Review': return { bg: '#FFF3E0', color: '#854F0B' }
    case 'Ready to Send Report': return { bg: '#E3F2FD', color: '#1565C0' }
    case 'Awaiting Client Acknowledgement': return { bg: '#FFF3E0', color: '#854F0B' }
    case 'Ready for WSHC Submission': return { bg: '#EDE7F6', color: '#5E35B1' }
    case 'Returned for Revision': return { bg: '#FDECEA', color: '#791F1F' }
    case 'Pending WSHC Outcome': return { bg: '#E3F2FD', color: '#1565C0' }
    case 'Completed': return { bg: '#EAF3DE', color: '#27500a' }
    case 'Draft': return { bg: '#f0f0f0', color: '#555' }
    default: return { bg: '#f0f0f0', color: '#555' }
  }
}

// Does this job need office action right now? (Dashboard surfacing.)
export const needsOfficeReview = job => workflowState(job) === 'Pending Office Review'
export const needsWshcOutcome = job => workflowState(job) === 'Pending WSHC Outcome'
